/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { ReactComponent as ZicassoSVG } from '../../assets/svg/zicasso.svg';
import './verification.scss';

interface VerifyFormValues {
  code: string[];
  initialCode?: string;
}

interface VerifyFormProps {
  email: string;
  isDesktop: boolean;
  onSubmit: (data: VerifyFormValues) => Promise<void>;
  submissionError?: ApiError;
  clearSubmissionError: () => void;
  onResendCode: () => Promise<void>;
  initialCode?: string;
}

const VERIFICATION_CODE_LENGTH = 4;
const RESEND_COOLDOWN_SECONDS = 60;

const VerifyForm = ({
  email,
  isDesktop,
  onSubmit,
  submissionError,
  clearSubmissionError,
  onResendCode,
  initialCode,
}: VerifyFormProps): ReactElement => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    setValue,
    watch,
    clearErrors,
  } = useForm<VerifyFormValues>({
    defaultValues: {
      code: initialCode && initialCode.length === VERIFICATION_CODE_LENGTH ? initialCode.split('') : Array(VERIFICATION_CODE_LENGTH).fill(''),
    },
    mode: 'onChange',
  });
  const code = watch('code');

  const [countdownSeconds, setCountdownSeconds] = useState(RESEND_COOLDOWN_SECONDS);
  const [isCountingDown, setIsCountingDown] = useState(false);

  useEffect(() => {
    if (submissionError) {
      setValue('code', Array(VERIFICATION_CODE_LENGTH).fill(''), { shouldValidate: true, shouldDirty: true });
    }
  }, [submissionError, setValue]);

  useEffect(() => {
    let timerId: NodeJS.Timeout;
    if (isCountingDown && countdownSeconds > 0) {
      timerId = setTimeout(() => {
        setCountdownSeconds((prev) => prev - 1);
      }, 1000);
    } else if (countdownSeconds === 0) {
      setIsCountingDown(false);
    }
    return () => clearTimeout(timerId);
  }, [isCountingDown, countdownSeconds]);

  const handleCodeChange = (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (!/^[0-9]$/.test(value) && value !== '') {
      return;
    }

    if (errors.code || submissionError) {
      clearErrors('code');
      if (submissionError) {
        clearSubmissionError();
      }
    }

    const newCode = [...code];
    newCode[index] = value;
    setValue('code', newCode, { shouldValidate: true, shouldDirty: true });

    if (value.length === 1 && index < VERIFICATION_CODE_LENGTH - 1) {
      const nextInput = document.getElementById(`code-input-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleResendAttempt = () => {
    if (isCountingDown) return;
    onResendCode();
    setCountdownSeconds(RESEND_COOLDOWN_SECONDS);
    setIsCountingDown(true);
  };

  return (
    <Stack
      spacing={3}
      sx={{
        width: isDesktop ? '100%' : '342px',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Stack flexDirection="column" spacing={4} alignItems="center" width="100%">
        <Stack spacing={4} alignItems="center">
          <ZicassoSVG aria-label="Zicasso" />
          <Box>
            <Typography variant="h2" sx={{ textAlign: 'center', mb: 2 }}>
              Verify Your Email to Complete Your Request
            </Typography>
            <Typography color="text.secondary" style={{ textAlign: 'center' }}>
              Please enter the four-digit code we just emailed to
              {' '}
              <strong>{email}</strong>
            </Typography>
          </Box>
        </Stack>

        <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
          <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 2, mb: 1 }}>
            {code.map((digit, index) => (
              <Controller
                key={`code-input-${index}`}
                name={`code.${index}` as const}
                control={control}
                rules={{ required: 'Verification code is required' }}
                render={({ field }) => (
                  <TextField
                    id={`code-input-${index}`}
                    variant="outlined"
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      handleCodeChange(index)(e as React.ChangeEvent<HTMLInputElement>);
                    }}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        width: '28px',
                        height: '28px',
                        padding: '10px',
                      },
                    }}
                    sx={{
                      width: '48px',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderRadius: '5px',
                          borderColor: errors.code || submissionError ? 'red' : '#070707',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: errors.code || submissionError ? 'red' : 'primary.main',
                        },
                      },
                    }}
                    error={!!errors.code || !!submissionError}
                  />
                )}
              />
            ))}
          </Stack>

          {submissionError && (
            <Typography color="error" variant="body2" sx={{ textAlign: 'center', mt: 1, mb: 1 }}>
              Invalid code: Please try again!
            </Typography>
          )}
          <Stack direction="column" spacing={1} alignItems="center" sx={{ mt: (errors.code ? 1 : 2), mb: 3 }}>
            {isCountingDown ? (
              <Typography variant="body2" color="text.secondary">
                You can resend the code in
                {' '}
                {countdownSeconds}
                s.
              </Typography>
            ) : (
              <Button
                onClick={handleResendAttempt}
                variant="text"
                sx={{
                  textTransform: 'none',
                  textDecoration: 'underline',
                  color: 'text.secondary',
                  fontSize: '1rem',
                  fontWeight: 400,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                    color: 'text.secondary',
                  },
                }}
              >
                Resend Code
              </Button>
            )}
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            Our team will only begin reviewing your request once you&apos;ve finalized these steps.
          </Typography>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            fullWidth
            disabled={(isSubmitting || !isValid)}
            sx={{
              py: 1.5, textTransform: 'none', fontSize: '1rem', mt: 4,
            }}
          >
            Continue
          </Button>
        </form>
      </Stack>
    </Stack>
  );
};

export default VerifyForm;
